<template>
  <div>
    <!-- Preference Cards -->
    <h4 v-if="!hidePreferenceCards" class="mb-1">
      Products selected by preference
    </h4>
    <div
      v-if="!hidePreferenceCards"
      class="preferences-grid mb-2"
    >
      <b-card v-if="isLoadingPreference">
        <LoadingContainer />
      </b-card>
      <div
        v-for="(item, index) in preferenceTableContent"
        v-else
        :key="index"
        class="preference-card"
        :class="{
          'preference-card-invalid': !isPreferenceInDropdown(item)
        }"
      >
        <div class="d-flex align-items-start">
          <b-avatar
            size="48"
            :variant="getPreferenceAvatarVariant(item)"
            rounded
            class="mr-2 flex-shrink-0"
          >
            <feather-icon
              :icon="getPreferenceIcon(item)"
              size="24"
            />
          </b-avatar>
          <div class="flex-grow-1">
            <h6 class="mb-1">
              {{ item.name }}
            </h6>
            <div
              v-if="isPreferenceInDropdown(item)"
              class="preference-details small"
            >
              <div
                v-for="header in preferenceTableHeaders"
                :key="header.key"
              >
                <template v-if="shouldShowProperty(header.key)">
                  <strong>{{ header.label }}:</strong> {{ item[header.key] }}
                </template>
              </div>
            </div>
            <div
              v-else-if="!isPreferenceInDropdown(item) && item.numbervalid !== undefined"
              class="preference-details small"
            >
              <div>
                <strong>Matches:</strong> {{ item.numbervalid }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <h4 class="mb-1 mt-2">
      Aggregate suitability information for all current products
    </h4>
    <b-row class="match-height mb-0">
      <b-col
        cols="12"
        md="6"
      >
        <b-card :class="{ 'category-card-with-hint': showCategoryHints }">
          <template #header>
            <hint-popup
              v-if="showCategoryHints"
              :id="20"
              class="category-hint-popup"
            />
            <div>
              <b-card-title>Properties Categories</b-card-title>
              <b-card-sub-title class="mt-0">Distribution of products by property characteristics</b-card-sub-title>
            </div>
          </template>
          <b-card-body>
            <div v-if="isLoadingSuitability || isRenderingCharts" style="min-height: 370px;">
              <loading-container />
            </div>
            <div v-else style="min-height: 370px; height: 370px;">
              <h5 class="text-center mt-0 mb-0">
                {{ propertiesChartTitle }}
              </h5>
              <app-echart-bar
                ref="propChart"
                :key="`properties-${selectedPreferenceId}-${selectedPropertiesIndex}`"
                :option-data="propertiesChartData"
                style="height: 100%; width: 100%;"
              />
            </div>

            <!-- Properties Chart Buttons -->
            <div
              class="d-flex flex-wrap justify-content-center mb-0 mx-n3"
              :class="mode === 'multi' ? 'mt-3' : 'mt-1'"
            >
              <b-button
                v-for="(item, index) in propertiesData"
                :key="index"
                :variant="getButtonVariant(item.bestlabelnode, selectedPropertiesIndex === index)"
                size="sm"
                class="mb-2 position-relative"
                style="flex: 0 0 18%; max-width: 18%; min-height: 50px; margin-left: 0.5rem; margin-right: 0.5rem;"
                @click="selectPropertiesChart(index)"
              >
                {{ item.name }}
                <feather-icon
                  v-if="item.bestlabelnode === 'Highest'"
                  icon="ChevronsUpIcon"
                  size="12"
                  class="position-absolute"
                  style="top: 2px; right: 2px;"
                />
                <feather-icon
                  v-else-if="item.bestlabelnode === 'Lowest'"
                  icon="ChevronsDownIcon"
                  size="12"
                  class="position-absolute"
                  style="top: 2px; right: 2px;"
                />
                <feather-icon
                  v-else
                  icon="MinusIcon"
                  size="12"
                  class="position-absolute"
                  style="top: 2px; right: 2px;"
                />
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        md="6"
      >
        <b-card :class="{ 'category-card-with-hint': showCategoryHints }">
          <template #header>
            <hint-popup
              v-if="showCategoryHints"
              :id="21"
              class="category-hint-popup"
            />
            <div>
              <b-card-title>Performance Categories</b-card-title>
              <b-card-sub-title class="mt-0">Distribution of products by performance metrics</b-card-sub-title>
            </div>
          </template>

          <b-card-body>
            <div v-if="isLoadingSuitability || isRenderingCharts" style="min-height: 370px;">
              <loading-container />
            </div>
            <div v-else style="min-height: 370px; height: 370px;">
              <h5 class="text-center mt-0 mb-0">
                {{ performanceChartTitle }}
              </h5>
              <app-echart-bar
                ref="perfChart"
                :key="`performance-${selectedPreferenceId}-${selectedPerformanceIndex}`"
                :option-data="performanceChartData"
                style="height: 100%; width: 100%;"
              />
            </div>

            <!-- Performance Chart Buttons -->
            <div
              class="d-flex flex-wrap justify-content-center mb-0 mx-n3"
              :class="mode === 'multi' ? 'mt-3' : 'mt-1'"
            >
              <b-button
                v-for="(item, index) in performanceData"
                :key="index"
                :variant="getButtonVariant(item.bestlabelnode, selectedPerformanceIndex === index)"
                size="sm"
                class="mb-2 position-relative"
                style="flex: 0 0 18%; max-width: 18%; min-height: 50px; margin-left: 0.5rem; margin-right: 0.5rem;"
                @click="selectPerformanceChart(index)"
              >
                {{ item.name }}
                <feather-icon
                  v-if="item.bestlabelnode === 'Highest'"
                  icon="ChevronsUpIcon"
                  size="12"
                  class="position-absolute"
                  style="top: 2px; right: 2px;"
                />
                <feather-icon
                  v-else-if="item.bestlabelnode === 'Lowest'"
                  icon="ChevronsDownIcon"
                  size="12"
                  class="position-absolute"
                  style="top: 2px; right: 2px;"
                />
                <feather-icon
                  v-else
                  icon="MinusIcon"
                  size="12"
                  class="position-absolute"
                  style="top: 2px; right: 2px;"
                />
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardBody,
  BCardSubTitle,
  BRow,
  BCol,
  BButton,
  BAvatar,
} from 'bootstrap-vue'
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import HintPopup from '@/views/components/HintPopup.vue'
import renderChartToBase64 from './charts/offscreenRender'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardBody,
    BCardSubTitle,
    BRow,
    BCol,
    BButton,
    BAvatar,
    AppEchartBar,
    LoadingContainer,
    HintPopup,
  },
  props: {
    showCategoryHints: {
      type: Boolean,
      default: false,
    },
    preferenceInfo: {
      type: Array,
      default: () => [],
    },
    suitabilityInfo: {
      type: Object,
      default: null,
    },
    selectedPreferenceId: {
      type: [String, Number, Boolean],
      default: null,
      validator: value => value === null || typeof value === 'string' || typeof value === 'number',
    },
    hidePreferenceCards: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'single',
      validator: value => ['single', 'multi'].includes(value),
    },
    isLoadingSuitability: {
      type: Boolean,
      default: false,
    },
    isLoadingPreference: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      propertiesChartData: null,
      performanceChartData: null,
      propertiesData: [],
      performanceData: [],
      selectedPropertiesIndex: 0,
      selectedPerformanceIndex: 0,
      cachedChartImages: {},
      isRenderingCharts: false,
    }
  },
  computed: {
    propertiesChartTitle() {
      if (this.propertiesData && this.propertiesData[this.selectedPropertiesIndex]) {
        return this.propertiesData[this.selectedPropertiesIndex].name || 'Properties Categories'
      }
      return 'Properties Categories'
    },
    performanceChartTitle() {
      if (this.performanceData && this.performanceData[this.selectedPerformanceIndex]) {
        return this.performanceData[this.selectedPerformanceIndex].name || 'Performance Categories'
      }
      return 'Performance Categories'
    },
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
    preferenceTableHeaders() {
      if (this.suitabilityInfo && this.suitabilityInfo.PREFERENCETABLE && this.suitabilityInfo.PREFERENCETABLE.header) {
        return this.suitabilityInfo.PREFERENCETABLE.header.map(h => ({
          key: h.key.toLowerCase(),
          label: h.label,
        }))
      }
      return []
    },
    preferenceTableContent() {
      if (this.suitabilityInfo && this.suitabilityInfo.PREFERENCETABLE && this.suitabilityInfo.PREFERENCETABLE.content) {
        return this.suitabilityInfo.PREFERENCETABLE.content
      }
      return []
    },
  },
  watch: {
    suitabilityInfo: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.CHARTINFO) {
          this.isRenderingCharts = true
          this.processChartData(newVal.CHARTINFO)
          this.isRenderingCharts = false
        } else {
          this.isRenderingCharts = true
        }
      },
    },
    skin() {
      // Re-process data when theme changes
      if (this.suitabilityInfo && this.suitabilityInfo.CHARTINFO) {
        this.processChartData(this.suitabilityInfo.CHARTINFO)
      }
    },
  },
  methods: {
    collectCharts() {
      return { ...this.cachedChartImages }
    },
    isPreferenceInDropdown(item) {
      // Check if this preference exists in the preferenceInfo array (the dropdown data)
      if (!item.preferenceid || !this.preferenceInfo) return false
      return this.preferenceInfo.some(p => p.preferenceid === item.preferenceid)
    },
    shouldShowProperty(key) {
      // Don't show preferenceid, name (used as header), or isvalid
      const lowerKey = key.toLowerCase()
      return lowerKey !== 'preferenceid' && lowerKey !== 'name' && lowerKey !== 'isvalid'
    },
    getPreferenceIcon(item) {
      const name = item.name ? item.name.toLowerCase() : ''

      if (name.includes('adventurous')) return 'ZapIcon'
      if (name.includes('growth')) return 'TrendingUpIcon'
      if (name.includes('income')) return 'PieChartIcon'
      if (name.includes('cautious')) return 'ShieldIcon'
      if (name.includes('moderate')) return 'AnchorIcon'

      return 'UserIcon' // Default icon
    },
    getPreferenceColor(item) {
      const name = item.name ? item.name.toLowerCase() : ''

      if (name.includes('adventurous')) return 'danger' // Red for high risk/adventurous
      if (name.includes('growth')) return 'success' // Green for growth
      if (name.includes('all products')) return 'primary' // Blue for all products
      if (name.includes('income')) return 'info' // Cyan for income
      if (name.includes('cautious')) return 'warning' // Amber for cautious
      if (name.includes('moderate')) return 'indicator' // yellow for moderate

      return 'primary' // Default color
    },
    getPreferenceAvatarVariant(item) {
      if (!this.isPreferenceInDropdown(item)) {
        return 'light-secondary'
      }

      const baseColor = this.getPreferenceColor(item)
      return `light-${baseColor}`
    },
    processChartData(chartInfo) {
      // Store all properties and performance data
      if (chartInfo.properties && chartInfo.properties.length > 0) {
        this.propertiesData = chartInfo.properties
        this.propertiesChartData = this.buildChartOption(this.propertiesData[this.selectedPropertiesIndex])
      }

      if (chartInfo.performance && chartInfo.performance.length > 0) {
        this.performanceData = chartInfo.performance
        this.performanceChartData = this.buildChartOption(this.performanceData[this.selectedPerformanceIndex])
      }

      this.prerenderChartImages()
    },
    prerenderChartImages() {
      const cache = {}
      const renderAll = (dataArray, prefix) => {
        dataArray.forEach((item, index) => {
          const b64 = renderChartToBase64(this.buildChartOption(item))
          if (b64) cache[`${prefix}${index + 1}`] = b64
        })
      }
      renderAll(this.propertiesData, 'propChart')
      renderAll(this.performanceData, 'perfChart')
      this.cachedChartImages = cache
      this.$emit('charts-ready')
    },
    selectPropertiesChart(index) {
      this.selectedPropertiesIndex = index
      this.propertiesChartData = this.buildChartOption(this.propertiesData[index])
    },
    selectPerformanceChart(index) {
      this.selectedPerformanceIndex = index
      this.performanceChartData = this.buildChartOption(this.performanceData[index])
    },
    getButtonVariant(bestlabelnode, isSelected) {
      if (bestlabelnode === 'Highest') {
        return isSelected ? 'success' : 'outline-success'
      }
      if (bestlabelnode === 'Lowest') {
        return isSelected ? 'danger' : 'outline-danger'
      }
      return isSelected ? 'secondary' : 'outline-secondary'
    },
    buildChartOption(chartData) {
      const categories = chartData.xaxis && chartData.xaxis.categories ? [...chartData.xaxis.categories] : []

      // For multi mode, use stacked bars
      if (this.mode === 'multi') {
        // Build grouped series from chartData.series (side by side bars)
        // First series (index 0) is "selected" in blue, rest are "not selected" in green
        const series = chartData.series ? chartData.series.map((serie, index) => ({
          name: serie.name || 'Products',
          type: 'bar',
          data: serie.data ? serie.data.map(v => (v && typeof v === 'object' && v.parsedValue !== undefined ? v.parsedValue : (Number(v) || 0))) : [],
          itemStyle: {
            color: index === 0 ? '#0050b3' : '#28c76f', // Blue for selected (first series), green for others
          },
        })) : []

        return {
          legend: {
            show: true,
            bottom: 0,
          },
          grid: {
            left: 70,
            right: 30,
            bottom: 70,
            top: 20,
            containLabel: false,
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow',
            },
          },
          xAxis: {
            type: 'category',
            data: categories,
            boundaryGap: true,
            axisLabel: {
              fontSize: 12,
              align: 'center',
              interval: 0,
              lineHeight: 16,
            },
            splitLine: {
              show: false,
            },
          },
          yAxis: {
            type: 'value',
            name: 'Number of Products',
            nameLocation: 'middle',
            nameGap: 50,
            nameTextStyle: {
              fontSize: 13,
              fontWeight: 500,
            },
            axisLabel: {
              fontSize: 12,
            },
            splitLine: {
              lineStyle: {
                opacity: this.splitLineOpacity,
                type: 'dashed',
              },
            },
          },
          series,
        }
      }

      // For single mode, combine all series data into one (original behavior)
      // Find the current index (non-zero value in series[0] - "current" series)
      let currentIndex = -1
      if (chartData.series && chartData.series.length > 0) {
        const currentSeries = chartData.series[0]
        currentIndex = currentSeries.data.findIndex(val => {
          if (val === null || val === undefined || val === 0) return false
          if (typeof val === 'object' && val.parsedValue !== undefined) return val.parsedValue !== 0
          return val !== 0
        })
      }

      // Combine all series data into one
      const combinedData = []

      // Add "Current" to the label at current index
      if (currentIndex !== -1 && categories.length > 0) {
        let suffix = '(Current'
        if (chartData.bestlabelnode === 'Highest' || chartData.bestlabelnode === 'Lowest') {
          suffix += ` ${chartData.bestlabelnode}`
        }
        suffix += ')'
        categories[currentIndex] = `${categories[currentIndex]}\n${suffix}`
      }

      const parseVal = v => {
        if (v === null || v === undefined) return 0
        if (typeof v === 'object' && v.parsedValue !== undefined) return v.parsedValue
        return Number(v) || 0
      }

      categories.forEach((_, index) => {
        let total = 0
        if (chartData.series) {
          chartData.series.forEach(serie => {
            total += parseVal(serie.data[index])
          })
        }

        // Color the current bar differently
        combinedData.push({
          value: total,
          itemStyle: {
            color: index === currentIndex ? '#0050b3' : '#28c76f', // Deep blue for current, green for others
          },
        })
      })

      return {
        legend: {
          show: false,
        },
        grid: {
          left: 70,
          right: 30,
          bottom: 70,
          top: 20,
          containLabel: false,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
        },
        xAxis: {
          type: 'category',
          data: categories,
          boundaryGap: true,
          axisLabel: {
            fontSize: 12,
            align: 'center',
            interval: 0,
            lineHeight: 16,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Number of Products',
          nameLocation: 'middle',
          nameGap: 50,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: [{
          name: 'Products',
          type: 'bar',
          data: combinedData,
          barWidth: '60%',
        }],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.category-card-with-hint {
  position: relative;
}

.category-card-with-hint ::v-deep .card-header {
  padding-right: 4rem;
}

.category-hint-popup {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
}

.preferences-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.preference-card {
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
}

.preference-card-invalid {
  opacity: 0.5;
  background-color: #f8f9fa;
}

.preference-details {
  margin-top: 0.5rem;
  line-height: 1.6;
}

.preference-details > div {
  margin-bottom: 0.25rem;
}

/* Dark Mode Support */
.dark-layout .preference-card {
  border-color: #3b4253;
  background-color: #283046;
}

.dark-layout .preference-card-invalid {
  background-color: #1e2433;
  opacity: 0.5;
}

/* Responsive */
@media (max-width: 991px) {
  .preferences-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
